import { Link } from "@inertiajs/react"
import { MapPin, Users } from "lucide-react"

import ContentActionMenu from "@/components/content-action-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { POSITIONS } from "@/lib/positions"

interface FeedOpportunity {
  id: number
  title: string
  description: string
  position_needed: string
  status: string
  country: string
  region: string | null
  division: string | null
  min_age: number | null
  max_age: number | null
  applications_count: number
  expires_at: string | null
  created_at: string
  club_profile: {
    id: number
    name: string
    slug: string
    user: {
      full_name: string
      avatar_url: string | null
    }
  }
}

export default function OpportunityFeedCard({
  opportunity,
}: {
  opportunity: FeedOpportunity
}) {
  const positionLabel =
    POSITIONS.find((p) => p.value === opportunity.position_needed)?.label ??
    opportunity.position_needed

  const ageRange =
    opportunity.min_age && opportunity.max_age
      ? `${opportunity.min_age}-${opportunity.max_age} años`
      : null

  const descriptionPreview =
    opportunity.description.length > 120
      ? opportunity.description.slice(0, 120) + "..."
      : opportunity.description

  return (
    <div className="border-border border-b px-4 py-4">
      {/* Author header — matches PostCard layout */}
      <div className="mb-3 flex items-center gap-3">
        <Avatar className="h-[55px] w-[55px] shrink-0">
          <AvatarImage
            src={opportunity.club_profile.user.avatar_url ?? undefined}
            alt={opportunity.club_profile.name}
            className="object-cover"
          />
          <AvatarFallback className="bg-krown-crown/20 text-krown-crown text-sm font-bold">
            {opportunity.club_profile.name[0]}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-bold text-white">
              {opportunity.club_profile.name}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-krown-crown text-[11px] tracking-wider uppercase">
              OPORTUNIDAD
            </span>
          </div>
        </div>
        <ContentActionMenu
          reportableType="Opportunity"
          reportableId={opportunity.id}
        />
      </div>

      {/* Title */}
      <h3 className="mb-1 text-base font-semibold text-white">
        {opportunity.title}
      </h3>

      {/* Description preview */}
      <p className="text-muted-foreground mb-3 text-sm">{descriptionPreview}</p>

      {/* Details row */}
      <div className="text-muted-foreground mb-3 flex flex-wrap gap-3 text-xs">
        <span className="bg-krown-200/20 text-krown-100 inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium">
          {positionLabel}
        </span>

        {opportunity.country && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {opportunity.region
              ? `${opportunity.region}, ${opportunity.country}`
              : opportunity.country}
          </span>
        )}

        {ageRange && <span>{ageRange}</span>}

        <span className="inline-flex items-center gap-1">
          <Users className="h-3 w-3" />
          {opportunity.applications_count} postulaciones
        </span>
      </div>

      {/* CTA */}
      <div className="flex items-center gap-3">
        <Link
          href={`/oportunidades/${opportunity.id}`}
          className="from-krown-orange-from to-krown-orange-to rounded-full bg-gradient-to-r px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          Postularme
        </Link>
        <Link
          href={`/oportunidades/${opportunity.id}`}
          className="text-krown-100 text-sm font-medium transition-colors hover:text-white"
        >
          Ver detalles
        </Link>
      </div>
    </div>
  )
}
